// src/pages/reports/CustomersReport.jsx
import { useState, useEffect, useMemo } from 'react'
import { ChevronDown, X } from 'lucide-react'
import BaseReportPage from '../../components/reports/BaseReportPage'
import BranchService from '../../services/branchService'

const CustomersFilters = ({ filters, onFiltersChange, onClear }) => {
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Load branches from API
  useEffect(() => {
    const loadBranches = async () => {
      try {
        setLoading(true)
        const response = await BranchService.getAllBranches()
        
        let branchesData = []
        if (response && response.success) {
          if (response.data && response.data.branches && Array.isArray(response.data.branches)) {
            branchesData = response.data.branches
          } else if (Array.isArray(response.data)) {
            branchesData = response.data
          }
        }
        
        // Include all branches (outlets only for customers)
        setBranches(branchesData.filter(branch => branch.type === 'outlet'))
      } catch (error) {
        console.error('Error loading branches:', error)
        setBranches([
          { _id: '609f1b8c8d2e4a1b2c3d4e5f', name: 'Main Branch', type: 'outlet' },
          { _id: '609f1b8c8d2e4a1b2c3d4e60', name: 'Downtown Branch', type: 'outlet' }
        ])
      } finally {
        setLoading(false)
      }
    }
    
    loadBranches()
  }, [])

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const handleClearFilters = () => {
    onFiltersChange({
      search: '',
      branch: ''
    })
    onClear()
  }

  const hasActiveFilters = useMemo(() => {
    return filters?.search || filters?.branch
  }, [filters])

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            <span>Clear Filters</span>
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Search Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search by ID, name, or mobile number..."
              value={filters?.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
          <div className="relative">
            <select
              value={filters?.branch || ''}
              onChange={(e) => handleFilterChange('branch', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10 appearance-none"
              disabled={loading}
            >
              <option value="">All Branches</option>
              {branches.map((branch) => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  )
}

const CustomersReport = () => {
  const columns = useMemo(() => [
    { key: 'customerId', label: 'Customer ID', sortable: true },
    { key: 'customerName', label: 'Customer Name', sortable: true },
    { key: 'mobileNumber', label: 'Mobile Number', sortable: true },
    { key: 'loyaltyPoints', label: 'Loyalty Points', sortable: true, type: 'number' },
    { key: 'redeemPoints', label: 'Redeem Points', sortable: true, type: 'number' },
    { key: 'remainingPoints', label: 'Remaining Points', sortable: true, type: 'number' },
    { key: 'totalOrders', label: 'Total Orders', sortable: true, type: 'number' },
    { key: 'totalPurchase', label: 'Total Purchase', sortable: true, type: 'currency' },
    { key: 'branch', label: 'Branch', sortable: true }
  ], [])

  return (
    <BaseReportPage
      title="Customers Report"
      subtitle="View customer loyalty points and purchase history"
      reportType="customers"
      columns={columns}
      customFilterComponent={CustomersFilters}
      enableFilters={true}
      fileName="customers-report"
    />
  )
}

export default CustomersReport